import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { ScrollView, StyleSheet } from 'react-native';

import { theme } from '@garnish/constants';
import { AnimatedViews, useResponsive } from '@garnish/core';

import { OrderLineItem, OrdersList } from '@order/features-orders';
import { useTelemetry } from '@order/Telemetry';
import { checkIfCanBeReorderedByCustomer } from '@order/utils';

import { ReorderOrderLineItem } from '../../../components';
import { type LineItem, type Order } from '../utils';
import { OrdersEmptyState } from './OrdersEmptyState';
import { OrdersListLoading } from './OrdersListLoading';
import { OrdersSeeMoreButton } from './OrdersSeeMoreButton';

export const OrdersListWrapper = (props: OrdersListWrapperProps) => {
  const {
    canReorder,
    orders,
    mostRecentOrder,
    isFetchingInitialOrders,
    isFetchingMoreOrders,
    canFetchMoreOrders,
    fetchMoreOrders,
  } = props;

  const router = useRouter();
  const { track } = useTelemetry();
  const { match } = useResponsive();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToOrderStatusScreen = React.useCallback(
    (orderId: string) => {
      router.navigate(`/order-status/${orderId}`);
    },
    [router],
  );

  const handleViewDetails = useCallback(
    (orderId: string) => {
      navigateToOrderStatusScreen(orderId);
      track('order_status.view', { entryPoint: 'account.orders' });
    },
    [navigateToOrderStatusScreen, track],
  );

  // ─── Derived ─────────────────────────────────────────────────────────

  const hasOrdersCard = isFetchingInitialOrders || orders.length > 0;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView
      scrollEventThrottle={16}
      style={match([null, hasOrdersCard ? styles.containerBorder : null])}
      contentContainerStyle={styles.scrollView}
    >
      <OrdersList.Container style={styles.orderList}>
        {isFetchingInitialOrders ? <OrdersListLoading /> : null}

        {!isFetchingInitialOrders && orders.length === 0 ? <OrdersEmptyState /> : null}

        {!isFetchingInitialOrders && orders.length > 0
          ? orders.map((order, index) => {
              const location = order.deliveryOrderDetail?.address?.name ?? order.restaurant.name;

              return (
                <AnimatedViews.Reveal key={order.id} delay={getRevealDelay(index)}>
                  <OrdersList.OrderContainer>
                    <OrdersList.OrderHeader
                      orderId={order.id}
                      date={order.wantedTime}
                      location={location}
                      onViewDetails={handleViewDetails}
                    />

                    <OrdersList.OrderLineItemsContainer>
                      {order.lineItems.map((lineItem) => {
                        return (
                          <LineItemWrapper
                            canReorder={canReorder}
                            key={lineItem.id}
                            lineItem={lineItem}
                            order={order}
                            mostRecentOrder={mostRecentOrder}
                          />
                        );
                      })}
                    </OrdersList.OrderLineItemsContainer>
                  </OrdersList.OrderContainer>
                </AnimatedViews.Reveal>
              );
            })
          : null}

        {canFetchMoreOrders ? (
          <OrdersSeeMoreButton
            isFetchingMoreOrders={isFetchingMoreOrders}
            fetchMoreOrders={fetchMoreOrders}
          />
        ) : null}
      </OrdersList.Container>
    </ScrollView>
  );
};

const LineItemWrapper = (props: LineItemWrapperProps) => {
  const { lineItem, order, mostRecentOrder, canReorder } = props;

  const canReorderLineItem = canReorder && checkIfCanBeReorderedByCustomer(lineItem);

  if (canReorderLineItem) {
    return (
      <ReorderOrderLineItem
        key={lineItem.id}
        lineItem={lineItem}
        orderDetails={order}
        mostRecentOrder={mostRecentOrder}
        location="order-history"
      />
    );
  }

  return <OrderLineItem lineItem={lineItem} orderDetails={order} />;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getRevealDelay(index: number) {
  return Math.min(index, MAX_STAGGERED_ORDERS) * ORDER_REVEAL_STAGGER;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ORDER_REVEAL_STAGGER = 65;
const MAX_STAGGERED_ORDERS = 4;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerBorder: {
    flexGrow: 0, // this is used to prevent the container (and the border) from taking the entire screen when there are only 1-3 orders.
    borderRadius: theme.radius.large,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
    overflow: 'hidden',
  },
  scrollView: {
    flexGrow: 1,
  },
  orderList: {
    marginTop: 0,
    marginBottom: 0,
  },
});

type OrdersListWrapperProps = Readonly<{
  canReorder: boolean;
  orders: readonly Order[];
  mostRecentOrder: Order;
  isFetchingInitialOrders: boolean;
  isFetchingMoreOrders: boolean;
  canFetchMoreOrders: boolean;
  fetchMoreOrders: () => void;
}>;

type LineItemWrapperProps = Readonly<{
  canReorder: boolean;
  order: Order;
  lineItem: LineItem;
  mostRecentOrder: Order;
}>;
